import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api } from "../lib/api";
import { authStore } from "../lib/authStore";
import { useAuth } from "../lib/auth";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { useI18n } from "../lib/i18n";
import { apiErrorMessage } from "../lib/apiErrors";
import { useDocumentTitle } from "../lib/useDocumentTitle";
import {
  AZ_PREFIX,
  e164Phone,
  formatLocalPhone,
  formatOtpTimer,
  localPhoneDigits,
  maskedPhone,
  onlyOtpDigits,
} from "../lib/phone";

const FOCUS = "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-espresso";

/**
 * Password recovery.
 *
 * Recovery runs over SMS, because the atelier has no mail provider: `SMTP_HOST`
 * is unset, so `POST /auth/password/forgot` accepted the address, answered 202,
 * and sent nothing. This page used to ask for an email and then report that a
 * link had been sent — the one recovery route in the product claimed to work
 * and delivered nothing.
 *
 * The backend already supported the honest version: an OTP with
 * `purpose: "reset"` proves the phone, and the verify call carries the new
 * password. That is what runs here.
 *
 * A link-borne `?token=` still works, so a reset issued by other means (an
 * operator, or a mail provider configured later) is not broken by this change.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  useDocumentTitle("Parolun bərpası");
  const { requestOtp, verifyOtp } = useAuth();
  const token = params.get("token") || "";

  // "token" = arrived from a link · "phone" → "code" = SMS recovery
  const [stage, setStage] = useState(token ? "token" : "phone");
  const [phoneDigits, setPhoneDigits] = useState(localPhoneDigits(params.get("phone") || ""));
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [resendSeconds, setResendSeconds] = useState(0);
  const [debugCode, setDebugCode] = useState("");
  const [sentTo, setSentTo] = useState("");
  const codeRef = useRef(null);

  const phone = e164Phone(phoneDigits);
  const phoneValid = Boolean(phone);

  useEffect(() => {
    if (resendSeconds <= 0) return undefined;
    const timer = window.setInterval(() => setResendSeconds((s) => Math.max(0, s - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [resendSeconds]);

  useEffect(() => {
    if (stage === "code") codeRef.current?.focus();
  }, [stage]);

  const passwordProblem = () => {
    if (password.length < 8) return t("activate.too_short", "Parol ən azı 8 simvol olmalıdır.");
    if (password !== confirm) return t("activate.mismatch", "Parollar uyğun gəlmir.");
    return "";
  };

  const sendCode = async (event) => {
    event?.preventDefault();
    if (!phoneValid || busy) return;
    setBusy(true);
    setError("");
    try {
      const response = await requestOtp(phone, "reset");
      setSentTo(response.phone || phone);
      setResendSeconds(response.resend_after_seconds || 60);
      setDebugCode(response.debug_code || "");
      setStage("code");
    } catch (requestError) {
      setError(apiErrorMessage(requestError, t));
    } finally {
      setBusy(false);
    }
  };

  /* The code is confirmed on its own, and the password is chosen afterwards.
     Both used to travel in one submit — tidy in principle, since there was no
     half-authenticated state, but the code lives 180 seconds and choosing a
     password takes longer than that more often than not. Customers were being
     told their code had expired because they had spent the time typing.

     The verified code issues a session; setting the password is then an
     ordinary authenticated change with no clock running. */
  const submitCode = async (event) => {
    event.preventDefault();
    if (busy) return;
    if (otp.length !== 6) {
      setError(t("reset.code_required", "6 rəqəmli kodu yazın."));
      return;
    }
    setBusy(true);
    setError("");
    try {
      await verifyOtp({ phone: sentTo || phone, code: otp, purpose: "reset" });
      setOtp("");
      setStage("password");
    } catch (requestError) {
      setError(apiErrorMessage(requestError, t));
    } finally {
      setBusy(false);
    }
  };

  const submitNewPassword = async (event) => {
    event.preventDefault();
    if (busy) return;
    const problem = passwordProblem();
    if (problem) { setError(problem); return; }
    setBusy(true);
    setError("");
    try {
      await api.post("/me/password", { password });
      navigate("/account", { replace: true });
    } catch (requestError) {
      setError(apiErrorMessage(requestError, t));
    } finally {
      setBusy(false);
    }
  };

  const submitToken = async (event) => {
    event.preventDefault();
    const problem = passwordProblem();
    if (problem) { setError(problem); return; }
    setError("");
    setBusy(true);
    try {
      const res = await api.post("/auth/password/reset", { token, password });
      const { access_token, refresh_token } = res.data.tokens || {};
      authStore.setTokens(access_token, refresh_token);
      navigate("/account", { replace: true });
    } catch (requestError) {
      // An expired or spent link is not a dead end — fall through to SMS.
      if (requestError?.response?.status === 400) {
        setStage("phone");
        setError(t("reset.link_expired", "Link vaxtı bitib. Telefon nömrənizlə davam edin."));
      } else {
        setError(apiErrorMessage(requestError, t));
      }
    } finally {
      setBusy(false);
    }
  };

  const passwordFields = (idPrefix) => (
    <>
      <label className="grid gap-2 adf-small text-[var(--adf-gray-3)]" htmlFor={`${idPrefix}-password`}>
        {t("activate.password", "Yeni parol")}
        <input
          id={`${idPrefix}-password`}
          type="password"
          placeholder="••••••••"
          autoComplete="new-password"
          data-testid="reset-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          className={`adf-auth-line-input ${FOCUS}`}
        />
      </label>
      <label className="grid gap-2 adf-small text-[var(--adf-gray-3)]" htmlFor={`${idPrefix}-confirm`}>
        {t("activate.confirm", "Parolu təkrarlayın")}
        <input
          id={`${idPrefix}-confirm`}
          type="password"
          placeholder="••••••••"
          autoComplete="new-password"
          data-testid="reset-confirm"
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
          className={`adf-auth-line-input ${FOCUS}`}
        />
      </label>
      <p className="m-0 text-[12px] leading-5 text-[var(--adf-gray-2)]">
        {t("reset.rule", "Ən azı 8 simvol.")}
      </p>
    </>
  );

  return (
    <div className="min-h-screen bg-[var(--adf-cream)] text-[var(--adf-ink)]">
      <Header />
      <main id="main" className="adf-container flex justify-center py-14 md:py-20">
        <div className="grid w-full max-w-[440px] gap-6">
          <button
            type="button"
            onClick={() => navigate("/auth/login")}
            className={`adf-auth-back ${FOCUS}`}
          >
            <ArrowLeft size={16} strokeWidth={1.5} aria-hidden="true" />
            {t("reset.back", "Girişə qayıt")}
          </button>

          <div className="grid gap-2">
            <span className="adf-small text-[var(--adf-espresso)]">{t("reset.eyebrow", "Hesabın bərpası")}</span>
            <h1 className="m-0 font-serif text-[34px] leading-tight">
              {t("reset.title", "Yeni parol təyin edin")}
            </h1>
            <p className="m-0 text-[14px] leading-6 text-[var(--adf-gray-2)]">
              {stage === "token"
                ? t("reset.intro", "Yeni parol seçin. Bütün digər cihazlardakı sessiyalar bağlanacaq.")
                : t("reset.intro_sms", "Nömrənizə birdəfəlik kod göndərəcəyik, sonra yeni parolu təyin edəcəksiniz. Bütün digər cihazlardakı sessiyalar bağlanacaq.")}
            </p>
          </div>

          {stage === "token" && (
            <form onSubmit={submitToken} className="grid gap-4" data-testid="reset-form">
              {passwordFields("token")}
              <button
                type="submit"
                disabled={busy}
                data-testid="reset-submit"
                className={`adf-cta adf-cta-dark min-h-[52px] w-full disabled:opacity-50 ${FOCUS}`}
              >
                {busy ? "…" : t("reset.submit", "Parolu yenilə")}
              </button>
            </form>
          )}

          {stage === "phone" && (
            <form onSubmit={sendCode} className="grid gap-4" data-testid="reset-phone-form">
              <label className="grid gap-2 adf-small text-[var(--adf-gray-3)]" htmlFor="reset-phone">
                {t("auth.phone_label", "Telefon nömrəsi")}
                <span className={`adf-auth-phone-control ${phoneValid ? "is-valid" : ""}`}>
                  <span className="adf-auth-country" aria-hidden="true">{AZ_PREFIX}</span>
                  <input
                    id="reset-phone"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel-national"
                    placeholder="(50) 123 45 67"
                    data-testid="reset-phone"
                    value={formatLocalPhone(phoneDigits)}
                    onChange={(event) => setPhoneDigits(localPhoneDigits(event.target.value))}
                  />
                </span>
              </label>
              <p className="m-0 text-[12px] leading-5 text-[var(--adf-gray-2)]">
                {t("auth.phone_help", "Azərbaycan mobil nömrəsini daxil edin.")}
              </p>
              <button
                type="submit"
                disabled={!phoneValid || busy}
                data-testid="reset-send-code"
                className={`adf-cta adf-cta-dark min-h-[52px] w-full disabled:opacity-50 ${FOCUS}`}
              >
                {busy ? "…" : t("auth.send_code", "Kodu göndər")}
              </button>
            </form>
          )}

          {stage === "code" && (
            <form onSubmit={submitCode} className="grid gap-4" data-testid="reset-code-form">
              <p className="m-0 text-[13px] leading-6 text-[var(--adf-gray-2)]" data-testid="reset-sent-to">
                {t("auth.code_sent", "Kod göndərildi")}: <b className="text-[var(--adf-ink)]">{maskedPhone(sentTo || phone)}</b>
              </p>
              <label className="grid gap-2 adf-small text-[var(--adf-gray-3)]" htmlFor="reset-code">
                {t("auth.code_label", "Təsdiq kodu")}
                <input
                  id="reset-code"
                  placeholder="000000"
                  ref={codeRef}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  data-testid="reset-code"
                  placeholder="000000"
                  value={otp}
                  onChange={(event) => setOtp(onlyOtpDigits(event.target.value))}
                  className={`min-h-[52px] border border-[var(--adf-gray-4)] bg-[var(--adf-white)] px-4 text-center text-[22px] tracking-[0.4em] text-[var(--adf-ink)] ${FOCUS}`}
                />
              </label>
              {debugCode && (
                <p className="m-0 text-[12px] text-[var(--adf-gray-3)]" data-testid="reset-debug-code">
                  {t("auth.debug_code", "Test kodu")}: {debugCode}
                </p>
              )}
              <button
                type="submit"
                disabled={busy || otp.length !== 6}
                data-testid="reset-submit"
                className={`adf-cta adf-cta-dark min-h-[52px] w-full disabled:opacity-50 ${FOCUS}`}
              >
                {busy ? "…" : t("reset.confirm_code", "Kodu təsdiqlə")}
              </button>
              <button
                type="button"
                onClick={sendCode}
                disabled={busy || resendSeconds > 0}
                data-testid="reset-resend"
                className={`adf-auth-text-action justify-self-start disabled:opacity-45 ${FOCUS}`}
              >
                {resendSeconds > 0
                  ? `${t("auth.resend_in", "Yenidən göndər")} ${formatOtpTimer(resendSeconds)}`
                  : t("auth.resend", "Kodu yenidən göndər")}
              </button>
            </form>
          )}

          {stage === "password" && (
            <form onSubmit={submitNewPassword} className="grid gap-4" data-testid="reset-password-form">
              <p className="m-0 text-[13px] leading-6 text-[var(--adf-gray-2)]">
                {t("reset.code_confirmed", "Kod təsdiqləndi. İndi yeni parolu təyin edin — vaxt məhdudiyyəti yoxdur.")}
              </p>
              {passwordFields("sms")}
              <button
                type="submit"
                disabled={busy}
                data-testid="reset-password-submit"
                className={`adf-cta adf-cta-dark min-h-[52px] w-full disabled:opacity-50 ${FOCUS}`}
              >
                {busy ? "…" : t("reset.submit", "Parolu yenilə")}
              </button>
            </form>
          )}

          {error && (
            <div role="alert" className="text-[13px] leading-6 text-[#8a3b2e]" data-testid="reset-error">
              {error}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
